'use client'

import { contactFormAction } from '@/lib/actions'
import { formSchema } from '@/lib/contact'
import {
  COMPANY_SIZE_OPTIONS,
  INTERESTED_IN_OPTIONS,
  ORDER_VOLUME_OPTIONS,
  USE_CASE_OPTIONS,
} from '@/lib/contact-form-options'
import { BlokProps } from '@/lib/types'
import { ContactFormStoryblok } from '@/lib/types/component-types-sb'
import { zodResolver } from '@hookform/resolvers/zod'
import { storyblokEditable } from '@storyblok/react'
import { CheckCircle2, HelpCircle, Server, Shield } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Suspense, useActionState, useEffect, useMemo } from 'react'
import { useForm, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Container } from '../shared/container'
import { DynamicForm } from '../shared/dynamic-form'
import { SectionHeader } from '../shared/section-header'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Checkbox } from '../ui/checkbox'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

function ConditionalUseCaseInputs() {
  const form = useFormContext()
  const useCase = useWatch({ control: form.control, name: 'useCase' })

  return (
    <AnimatePresence>
      {Array.isArray(useCase) && useCase.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
          animate={{ opacity: 1, height: 'auto', overflow: 'visible' }}
          exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
          transition={{ duration: 0.3 }}
        >
          <FormField
            control={form.control}
            name="orderVolume"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order volume</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select order volume per month" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ORDER_VOLUME_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="companySize"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company size</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {COMPANY_SIZE_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function ContactFormSkeleton() {
  return (
    <div className="space-y-6">
      {/* Interested in section */}
      <div className="space-y-2">
        <div className="bg-muted h-4 w-24 animate-pulse rounded" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="bg-muted h-16 w-full animate-pulse rounded-lg"
            />
          ))}
        </div>
      </div>

      {/* Company field */}
      <div className="space-y-2">
        <div className="bg-muted h-4 w-20 animate-pulse rounded" />
        <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
      </div>

      {/* First Name & Last Name fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="bg-muted h-4 w-20 animate-pulse rounded" />
          <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
        </div>
        <div className="space-y-2">
          <div className="bg-muted h-4 w-20 animate-pulse rounded" />
          <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
        </div>
      </div>

      {/* Email field */}
      <div className="space-y-2">
        <div className="bg-muted h-4 w-12 animate-pulse rounded" />
        <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
      </div>

      {/* Use case field */}
      <div className="space-y-2">
        <div className="bg-muted h-4 w-16 animate-pulse rounded" />
        <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
      </div>

      {/* Use case description */}
      <div className="space-y-2">
        <div className="bg-muted h-4 w-36 animate-pulse rounded" />
        <div className="bg-muted h-24 w-full animate-pulse rounded-md" />
      </div>

      {/* Checkboxes */}
      <div className="space-y-3">
        <div className="bg-muted h-5 w-3/4 animate-pulse rounded" />
        <div className="bg-muted h-5 w-full animate-pulse rounded" />
      </div>

      {/* Submit button */}
      <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
    </div>
  )
}

function InterestedInField() {
  const form = useFormContext()

  const icons = {
    MANAGED_HOSTING: Server,
    SUPPORT_SERVICES: HelpCircle,
    COMMERCIAL_EDITION: Shield,
  }

  const options = INTERESTED_IN_OPTIONS.map((opt) => ({
    ...opt,
    icon: icons[opt.value as keyof typeof icons],
  }))

  return (
    <FormField
      control={form.control}
      name="interestedIn"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Interested in</FormLabel>
          <div className="flex flex-col gap-2">
            {options.map((option) => {
              const isSelected = field.value.includes(option.value)

              const toggle = () => {
                const newValue = isSelected
                  ? field.value.filter((v) => v !== option.value)
                  : [...field.value, option.value]

                field.onChange(newValue)
              }

              return (
                <label
                  key={field.name + option.value}
                  className="bg-sidebar border-border flex cursor-pointer items-center justify-start gap-3 rounded-lg border px-3 py-2"
                >
                  <Checkbox
                    className="bg-background"
                    checked={isSelected}
                    onCheckedChange={toggle}
                  />

                  <option.icon className="text-brand hidden h-6 w-6 lg:block" />
                  <div className="flex flex-col">
                    <span className="text-accent-foreground text-sm font-medium lg:text-base">
                      {option.label}
                    </span>
                    <span className="text-muted-foreground text-xs lg:text-sm">
                      {option.description}
                    </span>
                  </div>
                </label>
              )
            })}
          </div>
          <FormDescription />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function ContactForm() {
  const searchParams = useSearchParams()

  // Parse interestedIn from search params
  const interestedInParam = searchParams.get('interestedIn')
  const initialInterestedIn = useMemo(() => {
    if (!interestedInParam) return []

    const validValues = ['MANAGED_HOSTING', 'SUPPORT_SERVICES', 'COMMERCIAL_EDITION']
    const paramValues = interestedInParam.split(',').map((v) => v.trim())

    return paramValues.filter((value) => validValues.includes(value))
  }, [interestedInParam])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      acceptTerms: false,
      interestedIn: initialInterestedIn,
      useCase: [],
      useCaseDescription: '',
      newsletterSubscribe: false,
      company: '',
      firstName: '',
      lastName: '',
    },
  })
  const [state, action, isPending] = useActionState(contactFormAction, {
    success: false,
    message: '',
  })

  useEffect(() => {
    if (state.success && state.message != '') {
      form.reset()
      toast.success(state.message)
    }
  }, [state.success])

  return (
    <div>
      {!state.success && state.message != '' && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {state.message}

            {state.errors && (
              <ul>
                {Object.keys(state.errors).map((key) => (
                  <li key={key}>{state.errors[key]}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}
      <DynamicForm
        data-testid="contact-form"
        action={action}
        isPending={isPending}
        form={form}
        fields={[
          {
            type: 'custom',
            name: 'interestedIn',
            component: InterestedInField,
          },
          {
            type: 'text',
            name: 'company',
            label: 'Company',
          },
          {
            fields: [
              {
                type: 'text',
                name: 'firstName',
                label: 'First Name',
              },
              {
                type: 'text',
                name: 'lastName',
                label: 'Last Name',
              },
            ],
          },
          {
            type: 'email',
            name: 'email',
            label: 'Email',
          },
          {
            type: 'multiselect',
            name: 'useCase',
            label: 'Use case',
            placeholder: 'Select one or multiple use cases',
            options: USE_CASE_OPTIONS.map((opt) => ({
              label: opt.label,
              value: opt.value,
            })),
          },
          {
            type: 'custom',
            name: 'useCaseInformation',
            component: ConditionalUseCaseInputs,
          },
          {
            type: 'textarea',
            name: 'useCaseDescription',
            label: 'Use case description',
            placeholder: 'Describe your use case with Vendure',
          },
          {
            type: 'checkbox',
            name: 'newsletterSubscribe',
            label: 'Subscribe to our monthly newsletter',
          },
          {
            type: 'checkbox',
            name: 'acceptTerms',
            label: (
              <p>
                I have read the{' '}
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  className="underline"
                >
                  privacy policy
                </Link>{' '}
                and accept the further processing of my personal data.
              </p>
            ),
          },
        ]}
        submitButton={{
          label: 'Send now',
        }}
      />
    </div>
  )
}

export function ContactFormSection({ blok }: BlokProps<ContactFormStoryblok>) {
  const options = useMemo(() => {
    return blok.options?.split('\n') ?? []
  }, [blok.options])

  return (
    <section
      {...storyblokEditable(blok)}
      className="border-border border-b"
    >
      <Container className="border-border border-x">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="space-y-6 p-6 lg:px-12 lg:py-16">
            <SectionHeader
              titleTag="h1"
              layout="stacked"
              preTitle={blok.descriptorTitle}
              title={blok.title}
              content={blok.content}
              icon={blok.descriptorIcon}
            />
            <div className="text-accent-foreground flex flex-col gap-2 font-medium lg:text-lg">
              {options.map((option) => (
                <div
                  key={option}
                  className="flex items-center justify-start gap-2"
                >
                  <CheckCircle2 className="text-muted-foreground fill-muted" />
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div className="px-6 pb-12 lg:px-12 lg:py-16">
            <Suspense fallback={<ContactFormSkeleton />}>
              <ContactForm />
            </Suspense>
          </div>
        </div>
      </Container>
    </section>
  )
}
